import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Zap, Trash2, Mail, Building2, MessageSquare, LogOut, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { verifyAdmin, fetchLeads, fetchStats, deleteLead } from "@/lib/apiClient";
import { Link } from "react-router-dom";

const STORAGE_KEY = "barraq_admin_key";

export default function Admin() {
  const [keyInput, setKeyInput] = useState("");
  const [adminKey, setAdminKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_interest: {} });
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) handleAuth(saved, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAuth = async (key, silent = false) => {
    setLoading(true);
    try {
      await verifyAdmin(key);
      setAdminKey(key);
      localStorage.setItem(STORAGE_KEY, key);
      await loadData(key);
      if (!silent) toast.success("Welcome back, operator.");
    } catch (e) {
      if (!silent) toast.error("Invalid admin key.");
      localStorage.removeItem(STORAGE_KEY);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async (key) => {
    try {
      const [l, s] = await Promise.all([fetchLeads(key), fetchStats(key)]);
      setLeads(l);
      setStats(s);
    } catch (e) {
      toast.error("Failed to load data.");
    }
  };

  const onLogin = (e) => {
    e.preventDefault();
    handleAuth(keyInput);
  };

  const onLogout = () => {
    setAdminKey(null);
    localStorage.removeItem(STORAGE_KEY);
    setKeyInput("");
    setLeads([]);
    setSelected(null);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await deleteLead(id, adminKey);
      toast.success("Lead deleted.");
      if (selected?.id === id) setSelected(null);
      await loadData(adminKey);
    } catch {
      toast.error("Failed to delete.");
    }
  };

  if (!adminKey) {
    return (
      <main
        className="min-h-screen bg-[#F7F5F0] flex items-center justify-center px-6 relative overflow-hidden"
        data-testid="admin-login-page"
      >
        <div className="aurora opacity-50" />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative w-full max-w-md bg-white border border-black/10 p-10"
        >
          <div className="flex items-center gap-3 mb-2">
            <Zap size={20} className="text-[#C99B1A] fill-[#C99B1A]" strokeWidth={2.5} />
            <span className="font-display font-bold text-lg tracking-tight text-[#0A0A0A]">
              BARRAQ<span className="text-[#C99B1A]">.</span>AI
            </span>
          </div>
          <div className="overline mb-2" style={{ color: "rgba(10,10,10,0.5)" }}>
            Operator Console
          </div>
          <h1 className="font-display text-3xl mb-8 tracking-tighter text-[#0A0A0A]">Authenticate.</h1>

          <form onSubmit={onLogin} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="overline block mb-3" style={{ color: "rgba(10,10,10,0.5)" }}>
                Admin Key
              </label>
              <input
                type="password"
                className="field-input"
                value={keyInput}
                onChange={(e) => setKeyInput(e.target.value)}
                placeholder="Enter admin key"
                required
                data-testid="admin-key-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center"
              data-testid="admin-login-button"
            >
              <ShieldCheck size={16} />
              {loading ? "Verifying..." : "Enter"}
            </button>
          </form>

          <Link
            to="/"
            className="block mt-8 text-xs uppercase tracking-[0.22em] text-black/40 hover:text-[#C99B1A] transition-colors"
          >
            ← Back to site
          </Link>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#F7F5F0] text-[#0A0A0A]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 bg-[#F7F5F0]/90 backdrop-blur-xl border-b border-black/10">
        <div className="container-x flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Zap size={18} className="text-[#C99B1A] fill-[#C99B1A]" />
            <span className="font-display font-bold tracking-tight">BARRAQ / OPS</span>
            <span className="text-black/30 font-mono text-xs ml-4">/admin/leads</span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="text-xs uppercase tracking-[0.22em] text-black/55 hover:text-[#C99B1A] transition-colors"
            >
              View Site
            </Link>
            <button
              onClick={onLogout}
              className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-black/55 hover:text-[#C99B1A] transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container-x py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/10 border border-black/10 mb-10">
          <div className="bg-white p-8" data-testid="stat-total">
            <div className="overline mb-3" style={{ color: "rgba(10,10,10,0.4)" }}>
              Total Leads
            </div>
            <div className="font-display text-5xl text-[#C99B1A] font-light">{stats.total}</div>
          </div>
          <div className="bg-white p-8">
            <div className="overline mb-3" style={{ color: "rgba(10,10,10,0.4)" }}>
              Top Interest
            </div>
            <div className="font-display text-2xl">
              {Object.entries(stats.by_interest || {}).sort((a, b) => b[1] - a[1])[0]?.[0] || "—"}
            </div>
          </div>
          <div className="bg-white p-8">
            <div className="overline mb-3" style={{ color: "rgba(10,10,10,0.4)" }}>
              Last Lead
            </div>
            <div className="font-display text-2xl">
              {leads[0]
                ? new Date(leads[0].created_at).toLocaleString("en-US", {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "—"}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 border border-black/10 bg-white">
            <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
              <h2 className="font-display tracking-tight">Inbox / {leads.length}</h2>
              <button
                onClick={() => loadData(adminKey)}
                className="text-xs uppercase tracking-[0.22em] text-black/55 hover:text-[#C99B1A]"
                data-testid="refresh-leads-button"
              >
                Refresh
              </button>
            </div>
            <div className="divide-y divide-black/10 max-h-[70vh] overflow-y-auto">
              {leads.length === 0 ? (
                <div className="p-10 text-center text-black/40 font-mono text-sm">No leads yet.</div>
              ) : (
                leads.map((lead) => (
                  <button
                    key={lead.id}
                    onClick={() => setSelected(lead)}
                    className={`w-full text-left p-6 hover:bg-black/[0.02] transition-colors ${
                      selected?.id === lead.id ? "bg-[#C99B1A]/[0.06] border-l-2 border-[#C99B1A]" : ""
                    }`}
                    data-testid={`lead-row-${lead.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="font-display text-lg">{lead.name}</div>
                      <div className="font-mono text-xs text-black/40">
                        {new Date(lead.created_at).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-black/55 mb-3">
                      <span className="flex items-center gap-1.5">
                        <Mail size={12} /> {lead.email}
                      </span>
                      {lead.company && (
                        <span className="flex items-center gap-1.5">
                          <Building2 size={12} /> {lead.company}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-xs uppercase tracking-[0.18em] px-2 py-1 border ${
                          lead.interest
                            ? "border-[#C99B1A]/40 text-[#C99B1A]"
                            : "border-black/15 text-black/40"
                        }`}
                      >
                        {lead.interest || "Unspecified"}
                      </span>
                      <p className="text-xs text-black/45 line-clamp-1 ml-4 flex-1 text-right">
                        {lead.message}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="border border-black/10 bg-white sticky top-24">
              {selected ? (
                <>
                  <div className="px-6 py-4 border-b border-black/10 flex items-center justify-between">
                    <h2 className="font-display tracking-tight">Detail</h2>
                    <button
                      onClick={() => onDelete(selected.id)}
                      className="text-black/40 hover:text-red-600 transition-colors"
                      data-testid={`delete-lead-${selected.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div className="p-6 space-y-6">
                    <div>
                      <div className="overline mb-2" style={{ color: "rgba(10,10,10,0.4)" }}>
                        Name
                      </div>
                      <div className="font-display text-2xl">{selected.name}</div>
                    </div>
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <div className="overline mb-2" style={{ color: "rgba(10,10,10,0.4)" }}>
                          Email
                        </div>
                        <a
                          href={`mailto:${selected.email}`}
                          className="text-[#C99B1A] hover:underline break-all"
                        >
                          {selected.email}
                        </a>
                      </div>
                      <div>
                        <div className="overline mb-2" style={{ color: "rgba(10,10,10,0.4)" }}>
                          Company
                        </div>
                        <div className="text-black/80">{selected.company || "—"}</div>
                      </div>
                      <div>
                        <div className="overline mb-2" style={{ color: "rgba(10,10,10,0.4)" }}>
                          Interest
                        </div>
                        <div className="text-black/80">{selected.interest || "Unspecified"}</div>
                      </div>
                    </div>
                    <div>
                      <div className="overline mb-2 flex items-center gap-2" style={{ color: "rgba(10,10,10,0.4)" }}>
                        <MessageSquare size={12} /> Message
                      </div>
                      <p className="text-black/75 leading-relaxed whitespace-pre-wrap">{selected.message}</p>
                    </div>
                    <div className="pt-4 border-t border-black/10 font-mono text-xs text-black/40">
                      Submitted {new Date(selected.created_at).toLocaleString()}
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-10 text-center text-black/40 font-mono text-sm">
                  Select a lead to view details.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}
